import { useMemo, type ReactNode } from 'react'
import { EQUIPMENT_SCOPE, ROOM_SCOPE, STORAGE_SCOPE, useEquipmentScope, type EquipmentScope } from '../equipmentScope'
import { useTableColumns } from '@/hooks/useTableColumns'
import { useSortedRows } from '@/hooks/useSortedRows'
import { useEventTypeCatalog } from '@/hooks/useEventTypes'
import type { SortAccessors } from '@/lib/tableSort'
import type { TableColumnDef } from '@/lib/tableColumnPrefs'
import { ColumnCells, ResizableTh } from '@/components/table'
import { Info, Link2, Pencil, Play, RotateCcw, Square } from 'lucide-react'
import { AssigneeSelect } from '@/components/procedure/AssigneeSelect'
import type { ProcedureAssignee } from '@/types/cgt'
import { cn } from '@/lib/utils'
import { overdueLabel } from '@/lib/maintenanceStatus'
import { DueDateEditor } from '../DueDateEditor'
import { clockTime, dayAndClock, formatDuration, localDayIso, localDaySpan } from '@/lib/timeWindow'
import {
  eventMeta,
  type EventTypeCatalog,
  TASK_CLASS_META,
  TASK_STATUS_META,
  type MaintenanceTask,
} from '@/types/maintenanceTask'

const HEAD =
  'whitespace-nowrap px-3 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wide'

// Local, not UTC: a UTC "today" makes the Today bolding wrong for anyone west
// of UTC from early evening onward.
function todayIso(): string {
  return localDayIso(new Date())
}

/**
 * The Due Date column: editable in place for a task that can still be moved (see
 * DueDateEditor). A booking has a window, not a due date, and a completed task
 * is done, so both are read-only, as is the cell without `onReschedule`.
 */
function DueDateCell({
  task,
  onReschedule,
}: {
  task: MaintenanceTask
  onReschedule?: (id: string, dueDate: string) => Promise<void>
}) {
  const editable = !!onReschedule && !task.window && task.status !== 'completed'
  return (
    <DueDateEditor
      dueDate={task.dueDate}
      earlyStart={task.earlyStart}
      subject={task.equipmentId}
      onSave={editable ? dueDate => onReschedule(task.id, dueDate) : undefined}
    >
      <DueDate task={task} />
    </DueDateEditor>
  )
}

/**
 * Due-date cell: "Today" (bold), else a short date. An overdue task adds a red
 * "Overdue · N days" line above the date, whatever its status (a started task
 * can be overdue), and a task finished late adds "Completed late" below it.
 *
 * A booking has no due date — it has a window — so it shows the period it
 * actually occupies the machine. A hold that crosses midnight names both days:
 * "09:00–15:00" on a two-day run reads as six hours.
 *
 * Out of service (z8nrz7fkgd): an overdue task on a unit that is out of service
 * says so, and a booking on one warns that it stands on a unit taking no new
 * bookings.
 */
function DueDate({ task }: { task: MaintenanceTask }) {
  const day =
    task.dueDate === todayIso() ? (
      <span className='font-semibold text-gray-900'>Today</span>
    ) : (
      <span className='text-gray-600'>
        {/* The YEAR is not optional here. Without it "Jul 24" reads as this
            year, so a task due in 2027 looks due next month — which is exactly
            how a task created today and dated years out passes for a normal
            one. Early Start has always shown the year; these two columns sit
            side by side and must be read the same way. */}
        {new Date(`${task.dueDate}T00:00:00`).toLocaleDateString(undefined, {
          month: 'short',
          day: 'numeric',
          year: 'numeric',
        })}
      </span>
    )

  if (task.window) {
    const { startsAt, endsAt } = task.window
    const multiDay = localDaySpan(startsAt, endsAt) > 0
    return (
      <span className='flex flex-col leading-tight'>
        {day}
        {/* Both ends, always. Showing only "→ Aug 5 13:30" left a reader unable
            to tell whether the machine goes down at 09:00 or 23:00 on day one. */}
        <span className='text-[11px] text-gray-400'>
          {multiDay
            ? `${clockTime(startsAt)} → ${dayAndClock(endsAt)}`
            : `${clockTime(startsAt)}–${clockTime(endsAt)}`}
        </span>
        {task.unitOutOfService && (
          <span
            title='The unit went out of service after this was booked. The booking stands; the unit takes no new bookings until it is back in service.'
            className='text-[11px] font-medium text-amber-700'
          >
            Unit out of service
          </span>
        )}
      </span>
    )
  }
  if (task.overdue) {
    return (
      <span className='flex flex-col items-start leading-tight'>
        <span className='font-semibold text-red-600'>{overdueLabel(task.daysOverdue)}</span>
        <span className='text-[11px]'>{day}</span>
        {task.unitOutOfService && (
          <span className='mt-1 inline-flex whitespace-nowrap rounded-full bg-gray-600 px-2 py-0.5 text-[10px] font-semibold text-white'>
            Out of service · overdue
          </span>
        )}
      </span>
    )
  }
  if (task.completedLate) {
    return (
      <span className='flex flex-col leading-tight'>
        {day}
        <span className='text-[11px] font-medium text-amber-700'>Completed late</span>
      </span>
    )
  }
  return day
}

function ClassBadge({ task }: { task: MaintenanceTask }) {
  const meta = TASK_CLASS_META[task.taskClass]
  return (
    <span
      title={meta.label}
      className='inline-flex h-6 w-6 items-center justify-center rounded-md text-xs font-bold'
      style={{ backgroundColor: `${meta.color}1f`, color: meta.color }}
    >
      {meta.glyph}
    </span>
  )
}

function EventChip({ code, catalog }: { code: string; catalog: EventTypeCatalog }) {
  const meta = eventMeta(code, catalog)
  return (
    <span className='inline-flex items-center gap-1.5 text-xs font-medium text-gray-700'>
      <span className='h-2 w-2 shrink-0 rounded-full' style={{ backgroundColor: meta.color }} />
      {meta.label}
    </span>
  )
}

function StatusPill({ task }: { task: MaintenanceTask }) {
  const meta = TASK_STATUS_META[task.status]
  return (
    <span
      className='inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-semibold'
      style={{ backgroundColor: `${meta.color}14`, color: meta.color }}
    >
      <span className='h-1.5 w-1.5 rounded-full' style={{ backgroundColor: meta.color }} />
      {meta.label}
    </span>
  )
}

function IconAction({
  title,
  onClick,
  children,
}: {
  title: string
  onClick?: () => void
  children: ReactNode
}) {
  return (
    <button
      type='button'
      title={title}
      onClick={onClick}
      className='inline-flex h-7 w-7 items-center justify-center rounded-md text-gray-400 transition-colors hover:bg-gray-100 hover:text-blue-600'
    >
      {children}
    </button>
  )
}

function RowActions({
  task,
  onStart,
  onEnd,
  onUndo,
  onInfo,
  onEdit,
  onPreviewForm,
}: {
  task: MaintenanceTask
  onStart: (id: string) => void
  onEnd: (id: string) => void
  onUndo: (id: string) => void
  onInfo: (id: string) => void
  onEdit: (id: string) => void
  onPreviewForm: (id: string) => void
}) {
  // A read-only row's lifecycle is owned elsewhere (a process step, not this
  // worklist), so the execution controls are absent rather than disabled —
  // there is no state in which they would become available here.
  if (task.readOnly) {
    return (
      <div className='flex items-center gap-1.5'>
        <IconAction title='Details' onClick={() => onInfo(task.id)}>
          <Info className='h-4 w-4' />
        </IconAction>
      </div>
    )
  }

  return (
    <div className='flex items-center gap-1.5'>
      {/* The task carries a form (z8nrz7fgzw): the icon previews it, as the
          Room Type grid's link does. In front of the buttons, in a slot every
          row keeps, so the buttons line up whether or not a task has one. */}
      <span className='inline-flex h-7 w-7 shrink-0 items-center justify-center'>
        {task.procedureTemplate && (
          <IconAction title={`Form: ${task.procedureTemplate.name} — preview`} onClick={() => onPreviewForm(task.id)}>
            <Link2 className='h-4 w-4 text-blue-600' />
          </IconAction>
        )}
      </span>
      {(task.status === 'due' || task.status === 'overdue') && (
        <button
          type='button'
          onClick={() => onStart(task.id)}
          disabled={!task.assigneeId}
          title={task.assigneeId ? undefined : 'Assign a technician before starting'}
          className={cn(
            'inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-semibold text-white shadow-sm transition-colors',
            task.assigneeId
              ? 'bg-green-600 hover:bg-green-700 active:scale-[0.98]'
              : 'cursor-not-allowed bg-gray-300 shadow-none',
          )}
        >
          <Play className='h-3.5 w-3.5 fill-current' />
          Start
        </button>
      )}
      {task.status === 'started' && (
        <button
          type='button'
          onClick={() => onEnd(task.id)}
          className='inline-flex items-center gap-1.5 rounded-md border border-red-200 bg-white px-3 py-1.5 text-xs font-semibold text-red-600 shadow-sm transition-colors hover:bg-red-50 active:scale-[0.98]'
        >
          <Square className='h-3.5 w-3.5 fill-current' />
          End
        </button>
      )}
      {/* A task an inventory action made ends with that action (z8nrz7fjzc). */}
      {task.status === 'completed' && !task.fromAction && (
        <button
          type='button'
          onClick={() => onUndo(task.id)}
          className='inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs font-medium text-gray-500 transition-colors hover:bg-amber-50 hover:text-amber-700'
        >
          <RotateCcw className='h-3.5 w-3.5' />
          Undo
        </button>
      )}
      <IconAction title='Details' onClick={() => onInfo(task.id)}>
        <Info className='h-4 w-4' />
      </IconAction>
      <IconAction title='Edit' onClick={() => onEdit(task.id)}>
        <Pencil className='h-4 w-4' />
      </IconAction>
    </div>
  )
}

// Per-user column layout. Equipment ID is locked (it identifies the task's unit)
// and so is the actions cell, which is the only way to start/end a task. See
// src/components/table/index.ts for the pattern.
// One layout per page (z8nrz7ewt3): the Cleaning page keeps 'room-my-list'.
const tableIdFor = (scope: EquipmentScope) => `${scope.tablePrefix}-my-list`

// Inventory's tasks are made by inventory actions (z8nrz7fjzc): no PM / CM
// Type, and what the task is for (a lot release, an order to receive) in place
// of an event type.
const columnsFor = ({ copy, tasksFromActions }: EquipmentScope): TableColumnDef[] => [
  ...(tasksFromActions ? [] : [{ id: 'type', header: 'Type', sortable: true }]),
  { id: 'equipmentId', header: copy.unitId, locked: true, sortable: true },
  { id: 'usedBy', header: 'Used By', sortable: true },
  { id: 'eventType', header: tasksFromActions ? 'Task' : 'Event Type', sortable: true },
  { id: 'priority', header: 'Prt', sortable: true },
  { id: 'earlyStart', header: 'Early Start', sortable: true },
  { id: 'dueDate', header: 'Due Date', sortable: true },
  { id: 'planHours', header: 'Plan Hrs', sortable: true },
  { id: 'assigned', header: 'Assigned', sortable: true },
  { id: 'status', header: 'Status', sortable: true },
  { id: 'actions', header: 'Actions', locked: true },
]

// Built once per page: the column-prefs hook keys its memo on array identity.
const COLUMNS = {
  equipment: columnsFor(EQUIPMENT_SCOPE),
  room: columnsFor(ROOM_SCOPE),
  storage: columnsFor(STORAGE_SCOPE),
} satisfies Record<EquipmentScope['variant'], TableColumnDef[]>

// Header sorting (z8nrz7e4h6). Unsorted, the worklist keeps the status →
// priority order MyListTab hands in. Event types sort by the tenant's names.
const myListSort = (catalog: EventTypeCatalog): SortAccessors<MaintenanceTask> => ({
  type: t => TASK_CLASS_META[t.taskClass].label,
  equipmentId: t => t.equipmentId,
  usedBy: t => t.usedBy,
  eventType: t => eventMeta(t.eventType, catalog).label,
  priority: t => t.priority,
  earlyStart: t => t.earlyStart,
  // A booking shows its window under the day, so it orders by the window start.
  dueDate: t => t.window?.startsAt ?? t.dueDate,
  planHours: t => t.planHours,
  assigned: t => t.assigned,
  status: t => TASK_STATUS_META[t.status].label,
})

const MY_LIST_HEAD_EXTRA: Record<string, string | undefined> = {
  type: 'w-14 text-center',
  priority: 'text-center',
  planHours: 'text-right',
  actions: 'text-right',
}

export function MyListTable({
  tasks,
  onStart,
  onEnd,
  onUndo,
  onInfo,
  onEdit,
  onPreviewForm,
  assign,
  onReschedule,
}: {
  tasks: MaintenanceTask[]
  onStart: (id: string) => void
  onEnd: (id: string) => void
  onUndo: (id: string) => void
  onInfo: (id: string) => void
  onEdit: (id: string) => void
  /** Show the forms a task carries, read-only (z8nrz7fgzw). */
  onPreviewForm: (id: string) => void
  /**
   * Assign from the row (z8nrz7f9u5), with the picker Transplant and the
   * procedure tables use. Absent for someone who cannot assign.
   */
  assign?: { users: ProcedureAssignee[]; meId?: string | null; onAssign: (id: string, assigneeId: string | null) => void }
  /** Change a task's due date from its row. Absent for someone who cannot edit tasks. */
  onReschedule?: (id: string, dueDate: string) => Promise<void>
}) {
  const scope = useEquipmentScope()
  const cols = useTableColumns(tableIdFor(scope), COLUMNS[scope.variant])
  const catalog = useEventTypeCatalog()
  const sortAccessors = useMemo(() => myListSort(catalog), [catalog])
  const rows = useSortedRows(tasks, cols.sort, sortAccessors)
  return (
    <>
    <div className='overflow-x-persistent rounded-lg border border-gray-200'>
      <table className='w-full min-w-[1100px] border-collapse text-sm'>
        <thead className='sticky top-0 z-10 bg-table-header text-table-header-foreground'>
          <tr>
            {cols.visible.map(({ def }) => (
              <ResizableTh
                key={def.id}
                columnId={def.id}
                columns={cols}
                label={def.header}
                className={cn(HEAD, MY_LIST_HEAD_EXTRA[def.id])}
                resizable={def.id !== 'actions'}
              >
                {def.header}
              </ResizableTh>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((task) => (
            <tr
              key={task.id}
              className={cn(
                'border-b border-gray-100 transition-colors hover:bg-blue-50/50',
                task.overdue && 'bg-red-50/40',
                task.status === 'completed' && 'text-gray-400',
              )}
            >
              <ColumnCells
                columns={cols}
                cells={{
                  type: (
                    <td className='px-3 py-3 text-center align-middle'>
                      <ClassBadge task={task} />
                    </td>
                  ),
                  equipmentId: (
                    <td className='px-3 py-3 align-middle font-semibold text-gray-900'>
                      {task.equipmentId}
                    </td>
                  ),
                  usedBy: <td className='px-3 py-3 align-middle text-xs text-gray-500'>{task.usedBy}</td>,
                  eventType: (
                    <td className='px-3 py-3 align-middle'>
                      <EventChip code={task.eventType} catalog={catalog} />
                    </td>
                  ),
                  priority: (
                    <td className='px-3 py-3 text-center align-middle text-gray-600'>
                      {task.priority ?? <span className='text-gray-300'>—</span>}
                    </td>
                  ),
                  earlyStart: (
                    <td className='whitespace-nowrap px-3 py-3 align-middle text-xs text-gray-500'>
                      {task.earlyStart
                        ? new Date(`${task.earlyStart}T00:00:00`).toLocaleDateString(undefined, {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric',
                          })
                        : '—'}
                    </td>
                  ),
                  dueDate: (
                    <td className='whitespace-nowrap px-3 py-3 align-middle text-sm'>
                      <DueDateCell task={task} onReschedule={onReschedule} />
                    </td>
                  ),
                  // A booked window is shown as a duration ("2d 6h"): the raw hour
                  // count is what makes a multi-day hold unreadable. Maintenance rows
                  // keep the planned-hours number they were estimated in.
                  planHours: (
                    <td className='px-3 py-3 text-right align-middle tabular-nums text-gray-700'>
                      {task.window ? formatDuration(task.planHours) : task.planHours.toFixed(1)}
                    </td>
                  ),
                  assigned: (
                    <td className='whitespace-nowrap px-3 py-3 align-middle text-xs text-gray-500'>
                      {/* A booking is not assignable here, and a completed task is a record. */}
                      {assign && !task.readOnly && task.status !== 'completed' ? (
                        <AssigneeSelect
                          users={assign.users}
                          value={task.assigneeId ? { id: task.assigneeId, name: task.assigned } : null}
                          meId={assign.meId}
                          className='w-40'
                          buttonClassName='h-8 text-xs'
                          onChange={(assigneeId) => {
                            if (assigneeId !== (task.assigneeId ?? null)) assign.onAssign(task.id, assigneeId)
                          }}
                        />
                      ) : (
                        task.assigned
                      )}
                    </td>
                  ),
                  status: (
                    <td className='px-3 py-3 align-middle'>
                      <StatusPill task={task} />
                    </td>
                  ),
                  actions: (
                    <td className='px-3 py-3 text-right align-middle'>
                      <div className='flex justify-end'>
                        <RowActions
                          task={task}
                          onStart={onStart}
                          onEnd={onEnd}
                          onUndo={onUndo}
                          onInfo={onInfo}
                          onEdit={onEdit}
                          onPreviewForm={onPreviewForm}
                        />
                      </div>
                    </td>
                  ),
                }}
              />
            </tr>
          ))}
          {tasks.length === 0 && (
            <tr>
              <td colSpan={cols.visible.length} className='px-3 py-12 text-center text-sm text-gray-400'>
                No tasks match this view. You're all caught up.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
    </>
  )
}
